import React, { useState, useRef, useEffect, useCallback } from 'react';
import Webcam from 'react-webcam';
import { Camera, Coffee, Clock, Play, X, AlertCircle } from 'lucide-react';
import axios from 'axios';

const MONITORING_INTERVAL_MS = 5000;

const CameraEngagementMonitor = ({ 
  videoRef,
  userId,
  moduleId,
  isActive = false
}) => {
  // State Management
  const webcamRef = useRef(null);
  const [cameraPermission, setCameraPermission] = useState(null);
  const [showCamera, setShowCamera] = useState(true);
  const [engagementScore, setEngagementScore] = useState(100);
  
  // Popup states
  const [showInterestPopup, setShowInterestPopup] = useState(false);
  const [isOnBreak, setIsOnBreak] = useState(false);
  const [breakTimeLeft, setBreakTimeLeft] = useState(600);
  const [showReadyPopup, setShowReadyPopup] = useState(false);
  const [readyTimeout, setReadyTimeout] = useState(20);
  
  // Intervals
  const monitoringInterval = useRef(null);
  const breakInterval = useRef(null);
  const readyTimeoutInterval = useRef(null);

  // ✅ FIX: Track consecutive low engagement
  const [lowEngagementCount, setLowEngagementCount] = useState(0);

  // ============================================
  // CAMERA PERMISSION
  // ============================================
  useEffect(() => {
    if (isActive) {
      requestCameraPermission();
    }
  }, [isActive]);

  const requestCameraPermission = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      stream.getTracks().forEach(track => track.stop());
      setCameraPermission(true);
      console.log('✅ Camera permission granted');
    } catch (err) {
      console.error('❌ Camera permission denied:', err);
      setCameraPermission(false);
    }
  };

  // ============================================
  // START/STOP MONITORING
  // ============================================
  const stopMonitoring = useCallback(() => {
    if (monitoringInterval.current) {
      clearInterval(monitoringInterval.current);
      monitoringInterval.current = null;
      setLowEngagementCount(0);
      console.log('⏸️ Monitoring stopped');
    }
  }, []);

  const captureAndAnalyze = useCallback(async () => {
    if (!webcamRef.current || !cameraPermission) {
      console.log('⚠️ Webcam not ready');
      return;
    }

    // ✅ FIX: Don't check if already showing popup or on break
    if (showInterestPopup || isOnBreak || showReadyPopup) {
      console.log('⏭️ Skipping check - popup/break active');
      return;
    }

    try {
      const imageSrc = webcamRef.current.getScreenshot();
      
      if (!imageSrc) {
        console.log('⚠️ Failed to capture image');
        return;
      }

      console.log('📸 Frame captured, analyzing...');

      const response = await axios.post('https://aivionlearn.aivion.ai/api/engagement/analyze-engagement', {
        user_id: userId,
        module_id: moduleId,
        frame: imageSrc,
        timestamp: new Date().toISOString()
      });

      const result = response.data;
      console.log('✅ Analysis result:', {
        score: result.engagement_score,
        is_bored: result.is_bored,
        should_show_popup: result.should_show_popup
      });

      setEngagementScore(result.engagement_score);

      // ✅ FIX: More aggressive popup triggering
      // Show popup if:
      // 1. Backend says to show popup (is_bored or looking_away)
      // 2. OR score is below 50 (lowered threshold)
      // 3. OR this is 2nd consecutive low score
      
      if (result.engagement_score < 50) {
        setLowEngagementCount(prev => prev + 1);
      } else {
        setLowEngagementCount(0);
      }

      const shouldTriggerPopup = 
        result.should_show_popup || 
        result.engagement_score < 50 || 
        lowEngagementCount >= 1; // After just 1 low score

      console.log('🎯 Popup decision:', {
        backend_says: result.should_show_popup,
        score_low: result.engagement_score < 50,
        consecutive_count: lowEngagementCount,
        will_show: shouldTriggerPopup
      });

      if (shouldTriggerPopup) {
        console.log('⚠️ TRIGGERING POPUP - User seems disengaged');
        if (videoRef?.current && !videoRef.current.paused) {
          videoRef.current.pause();
          console.log('⏸️ Video paused automatically');
        }
        setShowInterestPopup(true);
        setLowEngagementCount(0); // Reset counter
      }

    } catch (error) {
      console.error('❌ Analysis failed:', error);
      
      // ✅ FIX: Show popup even on error (assume disengagement)
      if (error.response?.status === 500 || error.code === 'ECONNREFUSED') {
        console.log('⚠️ Backend error - showing popup as safety measure');
        if (videoRef?.current && !videoRef.current.paused) {
          videoRef.current.pause();
          console.log('⏸️ Video paused automatically');
        }
        setShowInterestPopup(true);
      }
    }
  }, [cameraPermission, isOnBreak, lowEngagementCount, moduleId, showInterestPopup, showReadyPopup, userId, videoRef]);

  const startMonitoring = useCallback(() => {
    if (monitoringInterval.current) {
      clearInterval(monitoringInterval.current);
    }

    monitoringInterval.current = setInterval(() => {
      captureAndAnalyze();
    }, MONITORING_INTERVAL_MS);

    setTimeout(() => {
      captureAndAnalyze();
    }, MONITORING_INTERVAL_MS);

    console.log('🎬 Monitoring started - Checking every 5 seconds');
  }, [captureAndAnalyze]);

  useEffect(() => {
    if (isActive && cameraPermission) {
      startMonitoring();
    } else {
      stopMonitoring();
    }

    return () => stopMonitoring();
  }, [isActive, cameraPermission, startMonitoring, stopMonitoring]);

  // ============================================
  // VIDEO CONTROL
  // ============================================
  const pauseVideo = () => {
    if (videoRef?.current && !videoRef.current.paused) {
      videoRef.current.pause();
      console.log('⏸️ Video paused automatically');
    }
  };

  const resumeVideo = () => {
    if (videoRef?.current && videoRef.current.paused) {
      videoRef.current.play();
      console.log('▶️ Video resumed');
    }
  };

  // ============================================
  // USER RESPONSES
  // ============================================
  const handleInterestResponse = async (interested) => {
    setShowInterestPopup(false);

    try {
      await axios.post('https://aivionlearn.aivion.ai/api/engagement/user-interest-response', {
        user_id: userId,
        module_id: moduleId,
        interested: interested,
        timestamp: new Date().toISOString()
      });

      if (interested) {
        console.log('✅ User interested - Resuming');
        setEngagementScore(100);
        setLowEngagementCount(0); // Reset
        resumeVideo();
      } else {
        console.log('☕ Starting break');
        startBreak();
      }
    } catch (error) {
      console.error('❌ Failed to handle response:', error);
      // Continue anyway
      if (interested) {
        setEngagementScore(100);
        resumeVideo();
      } else {
        startBreak();
      }
    }
  };

  const startBreak = () => {
    setIsOnBreak(true);
    setBreakTimeLeft(600); // 10 minutes (600 seconds)
    // For testing: 60 seconds
    // setBreakTimeLeft(60);
    pauseVideo();
    stopMonitoring();

    if (breakInterval.current) {
      clearInterval(breakInterval.current);
    }

    breakInterval.current = setInterval(() => {
      setBreakTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(breakInterval.current);
          endBreak();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    console.log('☕ Break started - 10 minutes');
  };

  const endBreak = () => {
    console.log('⏰ Break ended - Showing ready popup');
    setIsOnBreak(false);
    showReadyToResumePopup();
  };

  const showReadyToResumePopup = () => {
    setShowReadyPopup(true);
    setReadyTimeout(20);

    if (readyTimeoutInterval.current) {
      clearInterval(readyTimeoutInterval.current);
    }

    readyTimeoutInterval.current = setInterval(() => {
      setReadyTimeout(prev => {
        if (prev <= 1) {
          clearInterval(readyTimeoutInterval.current);
          handleNoResponse();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    console.log('❓ Ready popup shown - 20 second timeout');
  };

  const handleNoResponse = () => {
    console.log('😴 No response - Snoozing for another 10 minutes');
    setShowReadyPopup(false);
    startBreak(); // Loop continues
  };

  const handleReadyResponse = (ready) => {
    if (readyTimeoutInterval.current) {
      clearInterval(readyTimeoutInterval.current);
    }

    setShowReadyPopup(false);

    if (ready) {
      console.log('✅ User ready - Resuming video and monitoring');
      setEngagementScore(100);
      setLowEngagementCount(0);
      resumeVideo();
      startMonitoring(); // Restart monitoring
    } else {
      console.log('⏰ User needs more time - Starting another break');
      startBreak();
    }
  };

  // ============================================
  // HELPERS
  // ============================================
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getScoreColor = () => {
    if (engagementScore > 70) return 'bg-green-500';
    if (engagementScore > 40) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  // Don't render if not active
  if (!isActive) return null;

  // Camera permission request
  if (cameraPermission === false) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50">
        <div className="bg-white rounded-2xl p-8 max-w-md mx-4 shadow-2xl">
          <div className="text-center">
            <Camera className="w-16 h-16 text-blue-500 mx-auto mb-4" />
            <h3 className="text-2xl font-bold mb-3">Camera Access Required</h3>
            <p className="text-gray-600 mb-6">
              We need camera access to monitor your learning engagement and suggest breaks when needed.
            </p>
            <button
              onClick={requestCameraPermission}
              className="w-full px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 font-semibold"
            >
              Enable Camera
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Loading
  if (cameraPermission === null) {
    return (
      <div className="fixed bottom-4 left-4 bg-white rounded-lg shadow-lg p-4 z-50">
        <p className="text-sm text-gray-600">Initializing camera...</p>
      </div>
    );
  }

  return (
    <>
      {/* Camera View */}
      {showCamera && cameraPermission && !isOnBreak && (
        <div className="fixed bottom-4 left-4 z-50">
          <div className="bg-white rounded-lg shadow-2xl overflow-hidden border-2 border-gray-300">
            <div className="relative">
              <Webcam
                ref={webcamRef}
                audio={false}
                screenshotFormat="image/jpeg"
                className="w-56 h-42 object-cover"
                videoConstraints={{
                  width: 640,
                  height: 480,
                  facingMode: "user"
                }}
                mirrored={true}
              />
              
              <div className="absolute top-2 left-2 bg-black bg-opacity-70 rounded-full px-3 py-1">
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${getScoreColor()} animate-pulse`} />
                  <span className="text-white text-xs font-bold">
                    {Math.round(engagementScore)}%
                  </span>
                </div>
              </div>

              {/* ✅ NEW: Low engagement warning */}
              {engagementScore < 50 && (
                <div className="absolute top-2 right-2 bg-orange-500 text-white px-2 py-1 rounded-full text-xs font-bold animate-pulse">
                  Low!
                </div>
              )}

              <button
                onClick={() => setShowCamera(false)}
                className="absolute bottom-2 right-2 bg-black bg-opacity-70 text-white p-1 rounded-full hover:bg-opacity-90"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="px-3 py-2 bg-gradient-to-r from-blue-50 to-purple-50">
              <p className="text-xs text-gray-700 font-medium">
                📹 Checking every 5s
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Show Camera Button */}
      {!showCamera && !isOnBreak && (
        <button
          onClick={() => setShowCamera(true)}
          className="fixed bottom-4 left-4 bg-blue-500 text-white p-4 rounded-full shadow-lg hover:shadow-xl z-50"
        >
          <Camera className="w-6 h-6" />
        </button>
      )}

      {/* Interest Popup */}
      {showInterestPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-8 max-w-md mx-4 shadow-2xl">
            <div className="text-center">
              <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-12 h-12 text-orange-600" />
              </div>
              
              <h3 className="text-2xl font-bold mb-3">Still Interested?</h3>
              
              <p className="text-gray-600 mb-2">
                Our AI detected you might be losing interest.
              </p>
              <p className="text-sm text-gray-500 mb-6">
                Engagement Score: <strong className="text-orange-600">{Math.round(engagementScore)}%</strong>
              </p>
              
              <div className="flex gap-3">
                <button
                  onClick={() => handleInterestResponse(true)}
                  className="flex-1 px-6 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 font-semibold transition-colors"
                >
                  ✅ Yes, Continue!
                </button>
                <button
                  onClick={() => handleInterestResponse(false)}
                  className="flex-1 px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 font-semibold transition-colors"
                >
                  ☕ Take Break
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Break Timer */}
      {isOnBreak && (
        <div className="fixed inset-0 bg-gradient-to-br from-blue-600 to-purple-700 flex items-center justify-center z-50">
          <div className="text-center text-white px-4">
            <Coffee className="w-24 h-24 mx-auto mb-6 animate-bounce" />
            <h2 className="text-4xl font-bold mb-4">Break Time! ☕</h2>
            <p className="text-xl mb-8">Relax for 10 minutes</p>
            
            <div className="bg-white bg-opacity-20 rounded-xl p-6 mb-6 backdrop-blur-sm inline-block">
              <p className="text-sm mb-2 flex items-center justify-center gap-2">
                <Clock className="w-4 h-4" />
                Time Remaining
              </p>
              <p className="text-6xl font-bold font-mono">{formatTime(breakTimeLeft)}</p>
            </div>
            
            <div className="w-64 mx-auto bg-white bg-opacity-20 rounded-full h-2">
              <div 
                className="bg-white h-full rounded-full transition-all"
                style={{ width: `${((600 - breakTimeLeft) / 600) * 100}%` }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Ready Popup */}
      {showReadyPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-8 max-w-md mx-4 shadow-2xl">
            <div className="text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Play className="w-12 h-12 text-green-600" />
              </div>
              
              <h3 className="text-2xl font-bold mb-3">Ready to Continue?</h3>
              <p className="text-gray-600 mb-4">Your break is over!</p>

              <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mb-6">
                <p className="text-sm text-orange-800">
                  ⏱️ Auto-snooze in <strong>{readyTimeout}s</strong> if no response
                </p>
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={() => handleReadyResponse(true)}
                  className="flex-1 px-6 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 font-semibold transition-colors"
                >
                  ✅ Yes, Ready!
                </button>
                <button
                  onClick={() => handleReadyResponse(false)}
                  className="flex-1 px-6 py-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600 font-semibold transition-colors"
                >
                  ⏰ More Time
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CameraEngagementMonitor;
